import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import {
  getWorkGroupUploaderLabel,
  isConflictCopyFileMetadata,
  useDownloadWorkGroupFileVersion,
  useRestoreFileVersion,
  useUploadWorkGroupFileVersion,
  useWorkGroupFileVersions,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

interface VersionHistoryDialogProps {
  workGroupId: string;
  fileId: string;
  fileName: string;
  folderId?: string;
  privateAccessReason?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VersionHistoryDialog({
  workGroupId,
  fileId,
  fileName,
  folderId,
  privateAccessReason,
  open,
  onOpenChange,
}: VersionHistoryDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { hasCapability } = usePermissions();
  const canRestore = hasCapability('workgroups.file.versions.restore');
  const canUploadVersion = hasCapability('workgroups.file.edit');
  const canDownload = hasCapability('workgroups.file.download');
  const { data: versions, isLoading, error } = useWorkGroupFileVersions(
    workGroupId,
    fileId,
    privateAccessReason,
    open,
  );
  const restoreMutation = useRestoreFileVersion();
  const uploadVersionMutation = useUploadWorkGroupFileVersion();
  const downloadVersionMutation = useDownloadWorkGroupFileVersion();

  const handleDownload = (versionId: string) => {
    downloadVersionMutation.mutate(
      {
        workGroupId,
        fileId,
        versionId,
        fileName,
        privateAccessReason,
      },
      {
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const handleRestore = (versionId: string) => {
    restoreMutation.mutate(
      {
        workGroupId,
        fileId,
        versionId,
        folderId,
        privateAccessReason,
      },
      {
        onSuccess: () => {
          toast({ title: 'Version restored' });
          onOpenChange(false);
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const handleUploadVersion = () => {
    if (!selectedFile) {
      toast({ title: 'Choose a file first', variant: 'destructive' });
      return;
    }
    uploadVersionMutation.mutate(
      {
        workGroupId,
        fileId,
        file: selectedFile,
        folderId,
        privateAccessReason,
      },
      {
        onSuccess: (uploadedFile) => {
          toast({ title: 'New version uploaded' });
          if (isConflictCopyFileMetadata(uploadedFile)) {
            toast({
              title: 'Conflict copy created',
              description:
                'A file with this name already existed, so WeWorkGroups saved this upload as a conflict copy.',
            });
          }
          setSelectedFile(null);
          if (inputRef.current) inputRef.current.value = '';
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setSelectedFile(null);
          if (inputRef.current) inputRef.current.value = '';
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <p className="text-sm text-muted-foreground truncate">{fileName}</p>
        </DialogHeader>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading versions…</p> : null}
        {error ? <p className="text-sm text-destructive">Could not load versions.</p> : null}
        {versions && versions.length > 0 ? (
          <ul className="max-h-64 space-y-2 overflow-auto">
            {versions.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <div>
                  <p className="text-foreground">
                    v{v.versionNumber}
                    {v.isCurrent ? (
                      <span className="ml-2 text-xs text-muted-foreground">(current)</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(v.createdAt).toLocaleString()} · {Math.round(v.sizeBytes / 1024)} KB
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {getWorkGroupUploaderLabel(v.uploadedBy)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                {canDownload ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={downloadVersionMutation.isPending}
                    onClick={() => handleDownload(v.id)}
                  >
                    Download
                  </Button>
                ) : null}
                {canRestore && !v.isCurrent ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={restoreMutation.isPending}
                    onClick={() => handleRestore(v.id)}
                  >
                    Restore
                  </Button>
                ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {!isLoading && !error && versions?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions found.</p>
        ) : null}
        {canUploadVersion ? (
          <div className="space-y-2 border-t border-border pt-3">
            <Label htmlFor="wg-version-upload">Upload new version</Label>
            <input
              ref={inputRef}
              id="wg-version-upload"
              type="file"
              className="block w-full text-sm text-foreground file:mr-4 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-2 file:text-sm"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              size="sm"
              disabled={!selectedFile || uploadVersionMutation.isPending}
              onClick={handleUploadVersion}
            >
              Upload version
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
